/**
 * My Projects View (Team Member)
 *
 * Read-only overview of projects where the user is assigned as a team member.
 * Provides a tabular view of project assignments with key details.
 */

import React from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { useMyProjects } from '../hooks/useProjects';
import { useReferenceData } from '../hooks/useReferenceData';
import { LoadingSpinner } from '../../../components/loading/LoadingStates';
import { ErrorDisplay } from '../../../components/errors/ErrorDisplay';

const MyProjects: React.FC = () => {
  const { projects, loading, error, loadProjects } = useMyProjects();
  const { referenceData } = useReferenceData();

  // Format date
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  // Get therapeutic area name from code
  const getTherapeuticAreaName = (code: string): string => {
    const ta = referenceData?.therapeuticAreas.find(ta => ta.code === code);
    return ta?.name || code;
  };

  // Get indication name from code
  const getIndicationName = (code: string): string => {
    const indication = referenceData?.indications.find(ind => ind.code === code);
    return indication?.name || code;
  };

  if (loading && projects.length === 0) {
    return <LoadingSpinner message="Loading your projects..." />;
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <ErrorDisplay
          title="Failed to load projects"
          error={error}
          onRetry={loadProjects}
        />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3,
        }}
      >
        <Box>
          <Typography variant="h4" sx={{ color: 'white' }}>Assigned Projects</Typography>
          <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
            Overview of your project assignments and contributions
          </Typography>
        </Box>
        <Tooltip title="Refresh">
          <IconButton onClick={loadProjects} disabled={loading} sx={{ color: 'white' }}>
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Projects Table */}
      {projects.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No projects assigned
          </Typography>
          <Typography variant="body2" color="text.secondary">
            You haven't been assigned to any projects yet
          </Typography>
        </Paper>
      ) : (
        <>
          <TableContainer component={Paper} sx={{ bgcolor: 'background.paper' }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell><strong>Project</strong></TableCell>
                  <TableCell><strong>Client</strong></TableCell>
                  <TableCell><strong>Product</strong></TableCell>
                  <TableCell><strong>TA / Indication</strong></TableCell>
                  <TableCell><strong>My Role</strong></TableCell>
                  <TableCell><strong>Contribution</strong></TableCell>
                  <TableCell><strong>Assigned Since</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {projects.map((project) => (
                  <TableRow key={project.projectId} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium" color="text.primary">
                        {project.projectName}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.primary">
                        {project.client}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.primary">
                        {project.product || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }} color="text.primary">
                        {getTherapeuticAreaName(project.therapeuticArea)}
                        {'\n'}
                        {project.indication ? getIndicationName(project.indication) : '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium" color="text.primary">
                        {project.role}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {project.contribution || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.primary">
                        {formatDate(project.assignedAt)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Summary */}
          <Box sx={{ mt: 2, textAlign: 'right' }}>
            <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
              Assigned to {projects.length} project{projects.length !== 1 ? 's' : ''}
            </Typography>
          </Box>
        </>
      )}
    </Box>
  );
};

export default MyProjects;
